import { dispatchAction } from '@/store/storeUtils';
import assetsApi from '@/api/assetsApi';
import authenticationService from '@/services/authenticationService';
import configurationsApi from '@/api/configurationsApi';
import remoteLogger from '@/api/remoteLoggerApi';
import router from '@/router/index';
import loginUtil from '@/screens/login/loginUtil';
import socketio from '@/plugins/socketio';
import axiosInitializer from '@/api/axiosInitializer';
import config from '@/common/config';
import DOMPurify from 'dompurify';

const TAG = 'lifecycle';
const ROOT = { root: true };

// Constants for redirect handling
const PENDING_REDIRECT_KEY = 'auth.pendingRedirect';
const REDIRECT_MAX_AGE = 30 * 60 * 1000; // 30 minutes

/**
 * Helper function to store the current URL for post-authentication redirect
 */
const storePendingRedirect = () => {
  const sanitizedSearch = DOMPurify.sanitize(window.location.search);
  const sanitizedHash = DOMPurify.sanitize(window.location.hash);
  const currentPath = window.location.pathname + sanitizedSearch + sanitizedHash;
  const isLoginPage = currentPath === '/' || currentPath.startsWith('/login');

  if (!isLoginPage && currentPath !== '/') {
    const redirectData = {
      url: currentPath,
      timestamp: Date.now(),
    };

    localStorage.setItem(PENDING_REDIRECT_KEY, JSON.stringify(redirectData));
  }
};

/**
 * Helper function to process and execute pending redirect after authentication
 * @returns {boolean} true if redirect was executed, false otherwise
 */
const processPendingRedirect = async () => {
  const pendingRedirect = localStorage.getItem(PENDING_REDIRECT_KEY);

  if (!pendingRedirect) {
    return false;
  }

  try {
    const { url, timestamp } = JSON.parse(pendingRedirect);
    const age = Date.now() - timestamp;

    if (age < REDIRECT_MAX_AGE) {
      await router.push(url);
      return true;
    }
    return false;
  } catch (err) {
    console.error('Failed to process pending redirect', err);
    return false;
  } finally {
    // Always remove to prevent redirect loops
    localStorage.removeItem(PENDING_REDIRECT_KEY);
  }
};

export const PHASES = {
  unknown: {
    name: 'unknown',
  },
  onStartup: {
    name: 'onStartup',

    preConditions: {
      nonPwa: {
        swRegistered: false,
        authenticated: false,
        configsLoaded: false,
        preCacheLoaded: false,
        isModuleConfigsProcessed: false,
        isDefaultLanguageLoaded: false,
        isLanguageLoaded: false,
      },
      pwa: {
        swRegistered: false,
        authenticated: false,
        configsLoaded: false,
        preCacheLoaded: false,
        isModuleConfigsProcessed: false,
        isDefaultLanguageLoaded: false,
        isLanguageLoaded: false,
      },
    },
    async enter({ rootState, commit, dispatch }) {
      //await config.loadInitialConfiguration();
      let msg = 'Phase "onStartup" entered';
      console.log(msg);
      console.log('- Awaiting service-worker is registered and pre-cache is loaded."');
      await remoteLogger.unauthorizedLog(TAG, msg);
      console.log('config instance during onStartup=', JSON.stringify(config, null, 2));
    },
  },
  bootstrapping: {
    name: 'bootstrapping',
    preConditions: {
      nonPwa: {
        authenticated: false,
        configsLoaded: false,
        swRegistered: false,
        preCacheLoaded: false,
        isModuleConfigsProcessed: false,
        isLanguageLoaded: false,
      },
      pwa: {
        swRegistered: true,
        preCacheLoaded: true,
        authenticated: false,
        configsLoaded: false,
        isModuleConfigsProcessed: false,
        isLanguageLoaded: false,
      },
    },

    /**
     * Bootstrap the application, that is
     * - Load default language
     * - Load configs of customer. Tenant is known after login only. If the user performs a login for a different tenant, the tenant specific file
     *   is loaded again after login.
     */
    enter: async ({ rootState, state, commit, dispatch }) => {
      let msg = 'Phase "bootstrapping" entered';
      console.log(msg);
      await remoteLogger.unauthorizedLog(TAG, msg);

      // IMPORTANT: this is the second time we load the initial configuration
      // Why? At the time of the first load (in config.js) the service worker is not registered yet
      // This means this request is not cached then.
      // Thus, we perform the request once more AFTER SW Registration to make sure it is cached.
      // Otherwise offline-capability would be broken
      await config.loadInitialConfiguration();
      console.log('config instance during bootstrapping=', JSON.stringify(config, null, 2));
      axiosInitializer.init();

      configurationsApi
        .loadPreAuthUiConfig()
        .then(async (preAuthConfigResponse) => {
          // load "untrusted" configurations, that is only customer but not tenant and user specific configuration gets loaded since the user is not authenticated yet
          // share the loaded configuration object with the responsive stores
          const preAuthConfig = preAuthConfigResponse.data;

          const { customerRelation, defaultLanguage, themes } = preAuthConfig;
          let storedTheme = localStorage.getItem('theme');
          commit('configs/storePreAuthConfig', { preAuthConfig }, ROOT);
          commit('theme/setThemes', { themes, applicableTheme: storedTheme ? storedTheme : 'light' }, ROOT);
          commit('profile/setCustomerRelations', { customerRelations: customerRelation }, ROOT);

          dispatch('language/loadDefaultLanguage', { lang: defaultLanguage }, ROOT);



          let features = rootState.configs.features;
          let cloudAuthProviderConfig = loginUtil.getCloudAuthProviderConfig(rootState.configs);

          let autoLogin = (cloudAuthProviderConfig && !cloudAuthProviderConfig.manualLoginEnabled) || localStorage.getItem('azure.auto_login');

          let credentials = await authenticationService.tryAuthentication(cloudAuthProviderConfig, features.rememberLogin, autoLogin);
          let responseCode = credentials.responseCode;

          if (responseCode === 'isAuthenticated') {
            // console.log('Valid (stored) login found while being in "bootstrapping" phase.');
            // console.log('Thus skipping "login" phase by setting state variables');

            localStorage.removeItem('azure.auto_login');

            dispatchAction('profile/setUser', credentials);
            dispatchAction('lifecycle/setAppStates', {
              configsLoaded: true,
              authenticated: true,
              logMsg: 'Valid stored login found, thus skipping "login" phase',
            });
          } else if (responseCode === 'loginRequired') {
            // no stored login found or not allowed to use.
            // Login will be displayed
            // Store the current URL for redirect after successful login
            storePendingRedirect();

            dispatch('setAppStates', {
              configsLoaded: true,
              logMsg: 'pre-auth configs loaded in bootstrap phase',
            });
          } else {
            // responseCode==='externalSignInStarted'
            console.log('external SignIn flow started');
            // Store the current URL for redirect after external authentication completes
            storePendingRedirect();
          }
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            console.log('auth exception - already handled by axiosInitializer => logout');
          } else {
            // TODO: Error Handling in case of server down
            //Status 502 (bad proxy) when server got a problem (docker container not running)
            console.warn('onPhaseBootstrapEntered failed. Recovered by using default application wide configs', err);

            if (
              confirm(
                'Die Applikation konnte nicht geladen werden da der API-Server nicht erreichbar ist.\n\nKlicken Sie auf "Ok" um die Applikation neu zu laden.'
              )
            ) {
              //dispatch('lifecycle/resetApp', {}, { root: true });
              window.location.reload();
            } else {
              console.log('Der User hat es abgelehnt, die App neu zu laden.');
            }
          }
        });
    },
  },
  login: {
    name: 'login', // this phase can be skipped in case a stored login is present

    preConditions: {
      nonPwa: {
        preCacheLoaded: false,
        swRegistered: false,

        configsLoaded: true,
        isLanguageLoaded: false,
        authenticated: false,
        isModuleConfigsProcessed: false,
      },
      pwa: {
        configsLoaded: true,
        swRegistered: true,
        authenticated: false,
        preCacheLoaded: true,
        isModuleConfigsProcessed: false,
        isLanguageLoaded: false,
      },
    },
    enter: async ({ rootState, state, commit, dispatch }) => {
      const msg = `Phase "login" entered`;
      // all actions to be done in Login.vue
      await remoteLogger.unauthorizedLog(TAG, msg);
    },
  },

  //
  authenticated: {
    name: 'authenticated', // when the user is authenticated either by an explicit login or a cached login

    preConditions: {
      nonPwa: {
        isLanguageLoaded: false,
        configsLoaded: true,
        authenticated: true,
        swRegistered: false,
        preCacheLoaded: false,
        isModuleConfigsProcessed: false,
      },
      pwa: {
        isLanguageLoaded: false,
        configsLoaded: true,
        swRegistered: true,
        authenticated: true,
        preCacheLoaded: true,
        isModuleConfigsProcessed: false,
      },
    },

    /**
     * * On successful login module configs get loaded and routes are configured based on config.
     * @param {*} param0
     */
    enter: async ({ rootState, state, commit, dispatch }) => {
      let msg = 'Phase "authenticated" entered';
      try {
        axiosInitializer.onSuccessfulLogin();
        await remoteLogger.cockpitLog({ message: 'user authenticated', messageType: 'authenticated' });
        await remoteLogger.log(TAG, msg);

        // Load pre-auth-ui-config to make sure this file is cached.
        // Although this request has completed in bootstrap phase, the service worker might not have been installed then
        // which would avoid caching of the resource
        await configurationsApi.loadPreAuthUiConfig();

        // Pre-load all assets available in tenants configuration assets folder to be sure all assets are getting
        // cached by service-worker
        // console.log("Pre-load all assets available in tenants configuration assets folder to be sure all assets are getting")
        // await assetsApi.loadAssets();

        commit('setPhase', { phase: PHASES.authenticated.name });

        await dispatch('configs/loadConfigurations', {}, ROOT);

        socketio.initializeSocketIo({ rootState, commit, dispatch, jwtToken: rootState.profile.user.token });

        dispatch('setAppStates', { authenticated: true, isModuleConfigsProcessed: true });
      } catch (err) {
        let response = confirm(
          "Applikation konnte nicht geladen werden. Die Applikation muss neu installiert werden. Klicken Sie auf 'ok', um einen neuen Versuch zu unternehmen."
        );
        if (response) {
          console.log('dispatch resetApp event');
          dispatch('lifecycle/resetApp', {}, { root: true });
        }
      }
    },
  },
  ready: {
    name: 'ready',
    preConditions: {
      nonPwa: {
        isLanguageLoaded: true,
        configsLoaded: true,
        authenticated: true,
        isModuleConfigsProcessed: true,
        swRegistered: false,
        preCacheLoaded: false,
      },
      pwa: {
        isLanguageLoaded: true,
        configsLoaded: true,
        authenticated: true,
        swRegistered: true,
        isModuleConfigsProcessed: true,
        preCacheLoaded: true,
      },
    },

    enter: async ({ rootState, state, commit, dispatch }) => {
      let msg = 'Phase "ready" entered';
      remoteLogger.log(TAG, msg);

      dispatch('startServerReachabilityCheck', {}, ROOT);
      dispatch('startUserAccessCheck', {}, ROOT);
      commit('setPhase', { phase: PHASES.ready.name });

      // IMPORTANT: this is the second time we load the initial configuration
      // Why? At the time of the first load (in config.js) the service worker is not registered yet
      // This means this request is not cached then.
      // Thus, we perform the request once more AFTER SW Registration to make sure it is cached.
      // Otherwise offline-capability would be broken
      await config.loadInitialConfiguration();
      console.log('config instance during ready phase=', JSON.stringify(config, null, 2));

      //
      // Pre-load all assets available in tenants configuration assets folder to be sure all assets are getting
      // cached by service-worker
      try {
        await assetsApi.loadAssets();
        // comment MS: already loaded in phase authenticated - I believe reloading is not necessary - however need to test with offline case
        // await dispatch('configs/loadConfigurations', {}, ROOT);
      } catch (err) {
        console.warn('Failed to load assets or configurations', err);
      }
      dispatch('sync/forceSynchronizeApp', { syncAfterAuthentication: true }, ROOT); // synchronize data but cache files only in pwa mode

      try {
        // Module Configs loaded => Register routes
        router.defineRoutes(rootState.configs, rootState.profile);

        // Check for pending redirect AFTER routes are registered
        // This ensures the target route exists before we try to navigate to it
        const redirected = await processPendingRedirect();

      } catch (err) {
        // TODO: What to do in this case?
        console.error('Failed to configure routes or process redirect', err);
      }
    },
  },
};
